import { WrappedFooter } from "@/comps/footer"
import Header from "@/comps/header"
import Meta from "@/comps/meta"
import PinnedMenu from "@/comps/pinned-menu"
import Tabs from "@/comps/tabs"

import { propsWithTranslation } from "@/ssr/common"

import Cookies from "@/comps/cookies"
import Favicon from "@/comps/favicon"

import {
	AdjustProvider,
	UsePageViewEvent,
} from "@/utils/adjust"

import { GlobalStyle } from "./careers"
import { AppProviderProps, queryClient } from "./_app"

import { ClientSideAuthProvider } from "@/utils/client-auth"
import { GoogleOneTap } from "@/utils/googleapi"
import { NotificationProvider } from "@/utils/notification"

import { ThemeProvider } from "next-themes"
import { QueryClientProvider } from "react-query"

import {
	Desktop,
	devices,
	Mobile,
	Tablet,
	TabletOrMobile,
} from "@/utils/breakpoints"
import { assetUrl } from "@/utils/cdn"

import { WalletProvider } from "@/utils/wallet"
import { useTranslation } from "next-i18next"
import {
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react"
import { Box } from "react-styled-flex"
import styled from "styled-components"

const Container = styled.div`
	position: relative;

	display: flex;
	flex-direction: column;
	align-items: center;

	width: var(--w-screen);

	padding-bottom: 32px;

	@media ${devices.tablet} {
		padding-bottom: 60px;
	}

	@media ${devices.desktop} {
		padding-bottom: 100px;
	}
`

const Title = styled.h1`
	font-weight: 700;
	font-size: 20px;
	line-height: 24px;

	margin: 25px 0 16px 0;

	@media ${devices.tablet} {
		font-weight: 700;
		font-size: 24px;
		line-height: 29px;

		margin: 25px 0;
	}
`

const DesktopRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 25px;

	margin: 20px 0 0 0;

	width: calc(var(--w-screen) - 32px);

	@media ${devices.tablet} {
		width: 605px;
	}

	@media ${devices.desktop} {
		width: 1175px;
		flex-direction: row;
	}
`

const FixedMobileMenuAligner = styled.div`
	position: fixed;
	z-index: 4;

	background-color: var(--color-background);
	top: 48px;
	left: 0;

	width: var(--w-screen);

	display: flex;
	flex-direction: column;
	align-items: center;

	@media ${devices.tablet} {
		top: 70px;
	}

	@media ${devices.desktop} {
		position: static;
		padding: 0;
		width: unset;
	}
`

const FixedMobileMenu = styled.div`
	align-self: center;

	position: relative;

	text-align: center;
	@media ${devices.tablet} {
		width: 605px;
	}

	@media ${devices.desktop} {
		width: unset;
	}
`

const ScrollGradient = styled.div`
	position: absolute;
	bottom: 0;
	right: -1px;

	width: 30px;
	height: 60px;

	z-index: 10;

	background: linear-gradient(
		to right,
		rgba(255, 0, 0, 0) 0%,
		var(--color-background) 100%
	);
`

const SectionsContainer = styled.div`
	position: relative;

	display: flex;
	flex-direction: column;

	gap: 8px;

	padding: 24px;
	border-radius: 10px;

	background-color: var(--color-background-cell);

	@media ${devices.tablet} {
		width: 605px;
	}

	@media ${devices.desktop} {
		width: 575px;
	}
`

const SingleTab = styled.div`
	font-weight: 600;
	font-size: 17px;
	line-height: 20px;

	padding: 20px 16px;

	flex-shrink: 0;
`

const FixedTabs = styled(Tabs)`
	@media ${devices.desktop} {
		width: 275px;
		background-color: var(--color-background-cell);
		border-radius: 10px;
		padding: 8px 0;
	}
`

const GoToContainer = styled.div<{
	show: boolean
	fixed: boolean
}>`
	position: ${(props) =>
		props.fixed ? "fixed" : "absolute"};
	z-index: 99;

	bottom: 24px;
	right: 24px;

	width: 48px;
	height: 48px;

	display: flex;
	cursor: pointer;

	border-radius: 100%;

	@media ${devices.desktop} {
		right: calc(50vw - 587px);
	}

	transition: opacity 300ms ease-in-out,
		background-color 300ms ease-in-out;
	opacity: ${(props) => (props.show ? 1 : 0)};

	pointer-events: ${(props) =>
		props.show ? "unset" : "none"};

	background-color: var(--color-primary-500);

	&:hover {
		background-color: var(--color-primary-600);
	}
`

const GoToIcon = styled.img`
	align-self: center;
	justify-self: center;
`

const Empty = styled.div<{ w: number; h: number }>`
	visibility: hidden;
	width: ${(props) => props.w}px;
	height: ${(props) => props.h}px;
`

const Styler = styled.div`
	* {
		line-height: 1.6em;
	}

	h1,
	h2,
	h3 {
		line-height: 1.2em;
	}

	h1 {
		margin-top: 32px;
		font-size: 40px;
	}

	h2 {
		font-size: 28px;
		text-transform: capitalize;
	}

	h2,
	h3 {
		margin-top: 24px;
		font-weight: 600;
	}

	h3 {
		font-size: 24px;
	}

	p {
		font-size: 16px;
		margin-top: 4px;
	}

	ol {
		padding: 8px 16px;

		li {
			margin-left: 16px;
			list-style-type: decimal;
		}
	}

	ul {
		padding: 8px 16px;

		li {
			margin-left: 16px;
			list-style-type: disc;
		}
	}

	a {
		text-decoration: underline;
	}

	& > :first-child {
		margin: 0;
	}
`

const PrivacyPolicy = () => {
	const { t } = useTranslation("page-policy")
	const ref = useRef<HTMLDivElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)

	const [tags, setTags] = useState<HTMLHeadingElement[]>([])

	const [showGoTo, setShowGoTo] = useState(false)
	const [fixed, setFixed] = useState(true)

	const goToShowHandler = useCallback(() => {
		const body = document.body,
			html = document.documentElement

		const height = Math.max(
			body.scrollHeight,
			body.offsetHeight,
			html.clientHeight,
			html.scrollHeight,
			html.offsetHeight,
		)

		const currentGoTo = height * 0.3 < window.scrollY
		if (currentGoTo !== showGoTo) setShowGoTo(currentGoTo)

		const maxFixedScroll = window.innerHeight
		const containerHeight =
			containerRef.current?.clientHeight

		if (!containerHeight) return
		const gotoFixed =
			containerHeight - window.scrollY >= maxFixedScroll

		if (gotoFixed !== fixed) setFixed(gotoFixed)
	}, [fixed, showGoTo])

	useEffect(() => {
		addEventListener("scroll", goToShowHandler)
		goToShowHandler()

		return () =>
			removeEventListener("scroll", goToShowHandler)
	}, [goToShowHandler])

	useEffect(() => {
		const section = ref.current
		if (!section) return
		const currentTags = [
			...section.getElementsByTagName("h2"),
		]

		currentTags.forEach((current) => {
			const preElement =
				current.previousElementSibling as HTMLPreElement
			if (!preElement) return
			const codeElement = preElement.querySelector(
				"code",
			) as HTMLPreElement
			if (!codeElement) return
			const label = codeElement.innerHTML
			current.setAttribute("data-label", label)
			preElement.hidden = true
		})

		setTags(currentTags)
	}, [])

	const scrollToTop = () => window.scrollTo({ top: 0 })

	return (
		<>
			<Container ref={containerRef}>
				<Meta
					title={t("common:txt_policy_title")}
					description={t("common:txt_policy_description")}
				/>
				<Header isStatic isSolid />
				<Desktop>
					<Title>{t("common:txt_terms_and_privacy")}</Title>
				</Desktop>
				<Mobile>
					<Box height={120} />
				</Mobile>
				<Tablet>
					<Box height={140} />
				</Tablet>
				<DesktopRow>
					<FixedMobileMenuAligner>
						<FixedMobileMenu>
							<Tablet>
								<ScrollGradient />
							</Tablet>
							<TabletOrMobile>
								<Title>
									{t("common:txt_terms_and_privacy")}
								</Title>
							</TabletOrMobile>
							<PinnedMenu container={ref}>
								<FixedTabs
									selected={0}
									tabs={tags.map((tag, index) => (
										<SingleTab key={index}>
											{tag.getAttribute("data-label")
												? tag.getAttribute("data-label")
												: tag.innerText}
										</SingleTab>
									))}
									refs={tags}
								/>
							</PinnedMenu>
						</FixedMobileMenu>
					</FixedMobileMenuAligner>
					<SectionsContainer ref={ref}>
						<Styler
							dangerouslySetInnerHTML={{
								__html: t("page-policy:page"),
							}}
						/>
					</SectionsContainer>
					<Desktop>
						<Empty w={275} h={10} />
					</Desktop>
				</DesktopRow>
				<GoToContainer
					onClick={scrollToTop}
					show={showGoTo}
					fixed={fixed}>
					<GoToIcon src={assetUrl("/icons/goto-top.svg")} />
				</GoToContainer>
			</Container>
			<WrappedFooter />
		</>
	)
}

export async function getStaticProps() {
	return await propsWithTranslation({}, ["page-policy"])
}

PrivacyPolicy.Layout = function Layout(
	props: AppProviderProps,
) {
	const { children, theme } = props

	return (
		<ThemeProvider attribute="class" forcedTheme={theme}>
			<GlobalStyle />

			<QueryClientProvider client={queryClient}>
				<ClientSideAuthProvider>
					<NotificationProvider timeout={3000}>
						<WalletProvider>
							<AdjustProvider>
								<Favicon />
								<Cookies />
								{children}
								<UsePageViewEvent />
								<GoogleOneTap />
							</AdjustProvider>
						</WalletProvider>
					</NotificationProvider>
				</ClientSideAuthProvider>
			</QueryClientProvider>
		</ThemeProvider>
	)
}

export default PrivacyPolicy
